import { useState, useEffect, useRef } from 'react';
import { IconPin } from './icons.jsx';

// ─────────────────────────────────────────────────────────────────────
// ReviewsSlider — premium herontwerp 2026-05-25.
//
// Zelfde editorial visual taal als AndereDienstenCarousel (plaag-cards):
//   • Witte ronde buiten-arrows met subtiele schaduw (geen overlay)
//   • Geen autoplay, geen dots/counter — gebruiker bepaalt tempo
//   • Touch swipe-support op mobiel
//   • Soft cubic-bezier transitions
//   • Editorial card-stijl: subtle border, hover-lift
//   • Asymmetrische header (eyebrow + H2 links, alle reviews-link rechts)
//
// Layout: 3 reviews/slide desktop, 2 tablet, 1 mobile.
// ─────────────────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  "#FF6B2B", "#1A2B4A", "#22C55E", "#8B5CF6", "#F59E0B",
  "#EF4444", "#06B6D4", "#10B981", "#EC4899", "#3B82F6",
];

function normalizeReview(r, i) {
  return {
    name: r.name || r.author || r.author_name || "Klant",
    loc: r.loc || r.locality || "",
    stars: r.stars || r.rating || 5,
    text: r.text || r.reviewBody || r.review || "",
    date: r.date || r.datePublished || r.relative_time_description || r.time || "",
    avBg: AVATAR_COLORS[i % AVATAR_COLORS.length],
    initial: (r.name || r.author || r.author_name || "K").charAt(0).toUpperCase(),
  };
}

export function ReviewsSlider({ reviews }) {
  // Defensive filter: lege review-text betekent lege kaart. google-reviews.js
  // filtert deze al weg, maar deze regel beschermt tegen callers die hun
  // eigen reviews-array doorgeven (bv. stad.jsx met statische sample).
  const items = (reviews || [])
    .filter(r => r && (r.text || r.reviewBody || r.review || "").trim().length > 0)
    .map(normalizeReview);
  const [perSlide, setPerSlide] = useState(3);
  const [slide, setSlide] = useState(0);
  const trackRef = useRef(null);
  const touchRef = useRef({ x: 0, active: false });

  // Responsive perSlide via matchMedia
  useEffect(() => {
    const setFromWidth = () => {
      const w = window.innerWidth;
      setPerSlide(w >= 1000 ? 3 : (w >= 640 ? 2 : 1));
    };
    setFromWidth();
    window.addEventListener('resize', setFromWidth);
    return () => window.removeEventListener('resize', setFromWidth);
  }, []);

  const totalSlides = Math.max(1, Math.ceil(items.length / perSlide));

  useEffect(() => {
    if (slide >= totalSlides) setSlide(0);
  }, [totalSlides, slide]);

  const next = () => setSlide(s => Math.min(s + 1, totalSlides - 1));
  const prev = () => setSlide(s => Math.max(s - 1, 0));

  // Touch swipe — zelfde patroon als plaag-carousel
  const onTouchStart = (e) => { touchRef.current = { x: e.touches[0].clientX, active: true }; };
  const onTouchEnd = (e) => {
    if (!touchRef.current.active) return;
    const dx = e.changedTouches[0].clientX - touchRef.current.x;
    if (Math.abs(dx) > 50) dx < 0 ? next() : prev();
    touchRef.current.active = false;
  };

  if (!items.length) return null;

  return (
    <section className="adc-section">
      <div className="container">
        {/* Asymmetrische header — exact zelfde als plaag-carousel */}
        <div className="adc-header">
          <div>
            <div className="adc-eyebrow">Klantbeoordelingen</div>
            <h2 className="adc-title">
              Wat klanten <em>over ons zeggen</em>
            </h2>
          </div>
          <a href="/reviews/" className="adc-all-link">
            Alle {items.length} reviews
            <span aria-hidden="true">→</span>
          </a>
        </div>

        <div className="adc-carousel">
          <div
            className="adc-viewport"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <div
              className="adc-track"
              ref={trackRef}
              style={{ transform: `translateX(-${slide * 100}%)` }}
            >
              {Array.from({ length: totalSlides }).map((_, si) => (
                <div key={si} className="adc-slide" data-per={perSlide}>
                  {items.slice(si * perSlide, si * perSlide + perSlide).map((r, ri) => (
                    <article key={ri} className="adc-review-card">
                      <div className="adc-review-top">
                        <div className="adc-review-av" style={{ background: r.avBg }}>{r.initial}</div>
                        <div className="adc-review-meta">
                          <div className="adc-review-name">{r.name}</div>
                          {r.loc ? (
                            <div className="adc-review-loc"><IconPin width="11" height="11" />&nbsp;{r.loc}</div>
                          ) : null}
                        </div>
                        {/* Google G-badge rechtsboven — toont herkomst */}
                        <div className="adc-review-g" aria-label="Google review" title="Google review">G</div>
                      </div>
                      <div className="adc-review-stars" aria-label={`${r.stars} van 5 sterren`}>
                        {Array.from({ length: 5 }).map((_, k) => (
                          <span key={k} style={{ opacity: k < r.stars ? 1 : .25 }}>★</span>
                        ))}
                      </div>
                      <p className="adc-review-text">"{r.text}"</p>
                      {r.date ? <div className="adc-review-date">{r.date}</div> : null}
                    </article>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {totalSlides > 1 && (
            <>
              <button
                className="adc-nav adc-nav-prev"
                onClick={prev}
                disabled={slide === 0}
                aria-label="Vorige reviews"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="15 18 9 12 15 6"/>
                </svg>
              </button>
              <button
                className="adc-nav adc-nav-next"
                onClick={next}
                disabled={slide === totalSlides - 1}
                aria-label="Volgende reviews"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </button>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
